<template>
  <b-overlay :show="loading" class="animated">
    <div class="d-flex justify-content-end">
      <b-button v-if="show_add_btn" variant="success" class="mb-3 mr-1" :to="`/submissions/${submission_id}/add-sample`">
        <i class="far fa-plus" />
      </b-button>
      
      <modal-trash-basket
        v-if="show_modal"
        :entity_type="entity_type"
        :trash="trash"
        @onUpdate="onUpdate"
      />
    </div>
    <v-server-table
      :columns="table_config.columns"
      :options="table_config.options"
      :theme="theme"
      :ref="ref"
      id="sample-server-table"
      class="w-100"
    >
      <div v-if="show_filters" slot="beforeTable" class="pt-3">
        <b-row>
          <b-col>
            <select-filter
              variant="info"
              icon="fa-toggle-on"
              placeholder="Quality Control"
              :list="[
                { value: 1, text: 'Yes - Quality Control' },
                { value: 0, text: 'No - Quality Control' },
              ]"
              :selected="show_quality_control"
              @onSelect="toggleColumn('quality_control.name', $event.value)"
            />
          </b-col>

          <b-col>
            <select-filter
              variant="info"
              :icon="sample_type.icon"
              placeholder="All Sample Types"
              :collection="sample_type.filters"
              value-key="id"
              text-key="name"
              filter-field="submission.sample_type_id"
              filter-operator="eq"
              @onSelect="setCustomFilter('sample_type', $event); fetchAnalytes(); fetchTestPackages()"
            />
          </b-col>
          <b-col class="p-0">
            <select-filter
              variant="info"
              :icon="analyte.icon"
              placeholder="All Analytes"
              :collection="analyte.filters"
              value-key="id"
              text-key="name"
              filter-field="submission.test_package.test_package_samples.test_package_analytes.analyte_id"
              filter-operator="eq"
              :disabled="!customFilters.sample_type"
              @onSelect="setCustomFilter('analyte', $event)"
            />
          </b-col>
          <b-col>
            <select-filter
              variant="info"
              :icon="test_package.icon"
              placeholder="All Test Packages"
              :collection="test_package.filters"
              value-key="id"
              text-key="name"
              filter-field="submission.test_package_id"
              filter-operator="eq"
              :disabled="!customFilters.sample_type"
              @onSelect="setCustomFilter('test_package', $event)"
            />
          </b-col>
          <b-col v-if="canReceive">
            <b-button block variant="success" @click="receiveAll"
              >Receiving All</b-button
            >
          </b-col>
        </b-row>
      </div>
      <span slot="pid" slot-scope="{ row }">
        <a :href="`#/samples/${row.id}`" class="btn-link">
          {{ row.publicNumber() }}
        </a>
      </span>
      <span slot="submission.pid" slot-scope="{ row }">
        <existence-gate :entity="row" :levels="['submission']" :field="'pid'">
          <template #existing-content>
            <router-link
              :to="`/submissions/${getData(row, {
                levels: ['submission'],
                field: 'id',
              })}`"
            >
              {{ row.getSubmission().publicNumber() }}
            </router-link>
          </template>
        </existence-gate>
      </span>
      <span slot="submission.project.customer.full_name" slot-scope="{ row }">
        <existence-gate
          :entity="row"
          :levels="table_data.customer"
          :field="'full_name'"
        >
          <template #existing-content>
            <router-link
              :to="`/customer/${getData(row, {
                levels: table_data.customer,
                field: 'id',
              })}`"
            >
              {{ row.submission.project.customer.full_name }}
              <!-- {{ getData(row, { levels: table_data.customer, field: "full_name" }) }} -->
            </router-link>
          </template>
        </existence-gate>
      </span>
      <span slot="created_at" slot-scope="{ row }">
        {{ row.created_at | moment("L") }}
      </span>
      <span slot="attribute" slot-scope="{ row }">
        <p v-b-tooltip.hover :title="getAttributeHover(row, 'attribute')">
          <template v-if="row.depth_start">
            {{ row.depth_start }} - {{ row.depth_end }}
          </template>
          <template v-else-if="row.usage">
            {{ row.usage }}
          </template>
          <template v-else-if="row.feed_type || row.animal_fed">
            {{ row.feed_type ? row.feed_type.value : '' }}
            {{ row.feed_type && row.animal_fed ? ' - ' : '' }}
            {{ row.animal_fed ? row.animal_fed.value : '' }}
          </template>
        </p>
      </span>
      <span slot="received" slot-scope="{ row }">
        <i v-if="hasStatus(row, 'Received')" class="fal fa-check" />
      </span>
      <span slot="prepped" slot-scope="{ row }">
        <i v-if="hasStatus(row, 'Prepped')" class="fal fa-check" />
      </span>
      <span slot="scheduled" slot-scope="{ row }">
        <i v-if="hasStatus(row, 'Scheduled')" class="fal fa-check" />
      </span>
      <span slot="tested" slot-scope="{ row }">
        <i v-if="hasStatus(row, 'Tested')" class="fal fa-check" />
      </span>
      <span slot="actions" slot-scope="{ row }">
        <a
          v-if="
            getData(row, { levels: table_data.status, field: 'status' }) ===
            'Sent'
          "
          class="btn btn-success px-1 py-0 text-white mr-1"
          @click="markReceived(row)"
        >
          <i class="fa fa-check" />
        </a>
        <a
          v-show="checkPermission('edit sample')"
          class="btn btn-warning px-1 py-0 text-white mr-1"
          :href="`#/samples/${row.id}/edit`"
        >
          <i class="fa fa-pencil" />
        </a>
        <template v-if="checkPermission('delete sample')">
          <a
            v-if="isDeleted(row.id)"
            class="btn btn-success px-1 py-0 text-white mr-1"
            @click="restoreFromTrash(row.id)"
          >
            <i class="fa fa-refresh" />
          </a>
          <a
            v-else
            class="btn btn-danger px-1 py-0 text-white mr-1"
            @click="putToTrash(row)"
          >
            <i class="fa fa-trash" />
          </a>
        </template>
      </span>
    </v-server-table>
  </b-overlay>
</template>

<script>
import Vue from "vue";
import BaseServerTable from "@/components/shared/BaseServerTable";
import ExistenceGate from "@/components/shared/ExistenceGate";
import SelectFilter from "@/components/shared/SelectFilter";
import SampleCollection from "@/collections/lab/SampleCollection";
import AnalyteModel from "@/models/lab/AnalyteModel";
import SampleStatusModel from "@/models/lab/SampleStatusModel";
import SampleTypeModel from "@/models/lab/SampleTypeModel";
import TestPackageModel from "@/models/lab/TestPackageModel";
import ToastNotifications from "@/components/shared/mixins/ToastNotifications";
import ModalTrashBasket from "@/components/shared/ModalTrashBasket";
import { AccessControlCheck } from "@/auth/AccessControlCheck";
import { ServerTable } from "vue-tables-2";
import { mapActions, mapGetters } from "vuex";
import { getCollection } from "@/shared/fetch-utils";
import { getEntityData } from "@/shared/utils";
import SampleExtensionTableDTO from "@/DTO/ExtensionTables/SampleExtensionTableDTO";

Vue.use(ServerTable);

export default {
  name: "SampleServerTable",
  extends: BaseServerTable,
  mixins: [ToastNotifications],
  components: {
    ExistenceGate,
    ModalTrashBasket,
    ServerTable,
    SelectFilter,
  },
  props: {
    collection: {
      type: Object,
      default: () => new SampleCollection(),
    },
    analytes: {
      type: Object,
      default: () => null,
    },
    sample_types: {
      type: Object,
      default: () => null,
    },
    test_packages: {
      type: Object,
      default: () => null,
    },
    sample_status: {
      type: Object,
      default: () => new SampleStatusModel(),
    },
    show_columns: {
      type: Array,
      default: () => [
        "pid",
        "receiving_id",
        "submission.pid",
        "submission.project.customer.full_name",
        "identifier",
        "number",
        "quality_control.name",
        "created_at",
        "attribute",
        "received",
        "prepped",
        "scheduled",
        "actions",
      ],
    },
    filterable: {
      type: Array,
      default: () => ["searchable"],
    },
    sortable: {
      type: Array,
      default: () => [
        "pid",
        "submission.pid",
        "submission.project.customer.full_name",
        "identifier",
        "number",
        "quality_control.name",
        "created_at",
      ],
    },
    show_filters: {
      type: Boolean,
      default: true,
    },
    show_add_btn: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const actions_access =
      this.checkPermission("edit sample") ||
      this.checkPermission("delete sample");

    return {
      entity_type: "sample",
      project_id: this.$route.params.project_id || null,
      test_package_id: this.$route.params.test_package_id || null,
      submission_id: this.$route.params.submission_id || null,
      analyte_collection: null,
      sample_type_collection: null,
      test_package_collection: null,
      table_config: Object.assign(this.getDefaultTableConfig(actions_access), {
        options: {
          headings: {
            pid: "#",
            receiving_id: "RID",
            "submission.pid": "Submission",
            "submission.project.customer.full_name": "Customer",
            identifier: "ID",
            number: "Num.",
            "quality_control.name": "QC",
            created_at: "Submitted",
            received: "Rec'd",
            scheduled: "Queued",
          },
          filterable: this.filterable,
          sortable: this.sortable,
          debounce: 300,
        },
      }),
      table_data: {
        customer: ["submission", "project", "customer"],
        status: ["current_sample_status_log", "sample_status"],
      },
      show_quality_control: 0,
    };
  },
  computed: {
    ...mapGetters({
      trash: "sampleTrash",
    }),
    analyte() {
      return {
        icon: `fa-${AnalyteModel.iconStatic()}`,
        filters: this.analytes || this.analyte_collection,
      };
    },
    sample_type() {
      return {
        icon: `fa-${SampleTypeModel.iconStatic()}`,
        filters: this.sample_types || this.sample_type_collection,
      };
    },
    test_package() {
      return {
        icon: `fa-${TestPackageModel.iconStatic()}`,
        filters: this.test_packages || this.test_package_collection,
      };
    },
    canReceive() {
      return (
        this.collection &&
        this.collection.models.length &&
        this.collection.models.filter((i) => !i.receiving_id).length
      );
    },
    show_modal() {
      return (
        this.checkPermission("delete sample") &&
        this.show_columns.includes("actions")
      );
    },
  },
  methods: {
    ...mapActions(["PUT_TO_TRASH", "RESTORE_FROM_TRASH"]),
    getData({ _reference }, data) {
      return getEntityData({
        entity: _reference,
        ...data,
      });
    },
    getAttributeHover(row, attribute) {
      return SampleExtensionTableDTO.getHoverTextStatic(row, attribute);
    },
    checkPermission(permission) {
      return AccessControlCheck.checkPermission(permission);
    },
    requestFunction(data) {
      this.applyFilterParams(data);
      this.loading = true;
      return new Promise((resolve, reject) => {
        if (this.sample_status && this.sample_status.id) {
          return this.collection
            .fetchByLatestStatus(this.sample_status)
            .then((response) => {
              this.loading = false;
              if (response && response.getStatus() === 200) {
                this.sampleModelMerge();
                resolve(this.collection);
              } else {
                reject();
              }
            });
        } else {
          return this.collection.fetch().then((response) => {
            this.loading = false;
            if (response && response.getStatus() === 200) {
              this.sampleModelMerge();
              resolve(this.collection);
            } else {
              reject();
            }
          });
        }
      });
    },
    markReceived(sample) {
      sample.setStatus("Received").then(
        () => {
          this.onUpdate();
          this.quickSuccessToast(
            "Sample Received",
            "The status was set successfully."
          );
        },
        (err) => {
          this.quickWarningToast("Sample Status Error", err);
        }
      );
    },
    markSent(sample) {
      this.$emit("onMarkSent");

      return sample.updateStatus("Received").then(() => {
        this.onUpdate();
        this.$emit("markedSent");
      });
    },
    receiveAll() {
      const requests = [];
      const update_list = this.collection.models.filter(
        (sample) =>
          sample.current_sample_status_log.sample_status.status !== "Received"
      );

      update_list.forEach((i) => {
        requests.push(i.setStatus("Received"));
      });
      Promise.all(requests).then(() => {
        this.onUpdate();
        this.quickSuccessToast(
          "Samples Received",
          "The status was set successfully."
        );
      });
    },
    hasStatus(sample, name) {
      let result = false;

      sample.getSampleStatusLogs().models.forEach((log) => {
        const status = log.getSampleStatus();

        if (status && status.status && status.status === name) {
          result = true;
        }
      });
      return result;
    },
    putToTrash(value) {
      this.PUT_TO_TRASH({ value, name: this.entity_type });
    },
    restoreFromTrash(id) {
      this.RESTORE_FROM_TRASH({ id, name: this.entity_type });
    },
    isDeleted(id) {
      return this.trash.find((item) => item.id === id);
    },
    onUpdate() {
      this.$refs[this.ref].refresh();
    },
    prepareCollectionFilter(filter) {
      filter.sortAddDesc("pid", true);
      this.project_id &&
        filter.filterEquals("submission.project_id", this.project_id);
      this.submission_id &&
        filter.filterEquals("submission_id", this.submission_id);
      this.test_package_id &&
        filter.filterEquals("submission.test_package_id", this.test_package_id);

      filter.connectionAdd("animal_fed");
      filter.connectionAdd("feed_type");
      filter.connectionAdd("quality_control");

      filter.filterEquals("submission.project.laboratory_id", this.$store.getters.getLaboratoryContext.id);

      return filter;
    },
    async init() {
      const payload = {
        sort: [
          {
            logic: "sortAddAsc",
            field: "name",
          },
        ],
        limit: "all",
        message: this.$bvToast,
      };

      !this.sample_types &&
        (this.sample_type_collection = await getCollection({
          ...payload,
          collection_type: "sample_type",
        }));
    },
    async fetchAnalytes() {
      this.setCustomFilter('analyte', null)
      if (this.analytes) return;
      this.analyte_collection = null

      let filters = []

      if (this.customFilters.sample_type) {
        filters.push({
          field: "sample_type_id",
          logic: "filterEquals",
          value: this.customFilters.sample_type.value
        })
      }

      this.analyte_collection = await getCollection({
        sort: [
          {
            logic: "sortAddAsc",
            field: "name",
          },
        ],
        filters,
        limit: "all",
        message: this.$bvToast,
        collection_type: "analyte",
      });
    },
    async fetchTestPackages() {
      this.setCustomFilter('test_package', null)
      if (this.test_packages) return
      this.test_package_collection = null

      let filters = []

      if (this.customFilters.sample_type) {
        filters.push({
          logic: "filterEquals",
          field: "sample_type_id",
          value: this.customFilters.sample_type.value,
        })
      }

      this.test_package_collection = await getCollection({
        sort: [
          {
            logic: "sortAddAsc",
            field: "name",
          },
        ],
        filters,
        limit: "all",
        message: this.$bvToast,
        collection_type: "test_package",
      })
    },
  },
  created() {
    this.init();

    this.toggleColumn("quality_control.name", this.show_quality_control);
  },
};
</script>
